/*
 *  -----------------------------------------------------------------------
 *    <copyright file="step2-line-content.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { forwardRef, useImperativeHandle, useMemo, useState, useEffect } from 'react';
import FormControl from '@mui/material/FormControl';
import { Step2Props } from './interfaces';
import { useBoolean, useLocalState, useTranslation } from 'hooks';
import { PanelFields_Panel_graph_PanelGraph_series_PanelGraphSeries as PanelFields_graph_series } from 'queries/generated/fragments.generated';
import {
    PanelAverageLineThresholdType,
    PanelDisplayUnits,
    PanelLineCalculation,
    PanelLineTimePeriod,
    PanelSeriesType,
    RowDataSourceTable,
    RowDataSourceTag,
} from 'types/graphql-global-types';
import { checkRequiredFields, validateFields } from 'utils/validation';
import DataSourceSelector from 'views/components/central-monitoring/data-source-selector';
import { FieldSelector, Formats } from 'views/components/central-monitoring/field-selector';
import PanelService from 'views/components/central-monitoring/panels/add-panel/service';
import CharactersRemaining from 'views/components/core/characters-remaining';
import Loading from 'views/components/core/loading';
import OutlinedWrapper from 'views/components/core/outlined-wrapper';
import SelectWithCategories from 'views/components/core/select-with-categories';
import TextField from 'views/components/core/text-field';
import 'views/components/central-monitoring/panels/add-panel/styles.scss';
import Constants from '@utils/constants';

const graphNumberOfSeries = [1, 2, 3, 4];

interface Step2LineProps extends Step2Props {
    setGraphSeriesState: (seriesNumber: number, newSeries: PanelFields_graph_series) => void;
    clearGraphSeriesState: (fromSeriesNumber: number) => void;
}

const Step2Line = forwardRef(
    (
        {
            panelState,
            setGraphSeriesState,
            clearGraphSeriesState,
            setPage,
            page,
            show,
            updateState,
            updateErrorState,
            canSave,
            scheduleOptions,
        }: Step2LineProps,
        ref,
    ) => {
        const { t } = useTranslation();
        const { state: localState } = useLocalState();

        const initialState = {
            timePeriod: panelState.graph.timePeriod,
            seriesType: panelState.graph.seriesType,
            numberOfSeries: panelState.graph.numberOfSeries,
            series1label: panelState.graph.series?.length > 0 ? panelState.graph.series[0].label : '',
            series2label: panelState.graph.series?.length > 1 ? panelState.graph.series[1].label : '',
            series3label: panelState.graph.series?.length > 2 ? panelState.graph.series[2].label : '',
            series4label: panelState.graph.series?.length > 3 ? panelState.graph.series[3].label : '',
            series1threshold: panelState.graph.series?.length > 0 ? panelState.graph.series[0].threshold : '',
            series2threshold: panelState.graph.series?.length > 1 ? panelState.graph.series[1].threshold : '',
            series3threshold: panelState.graph.series?.length > 2 ? panelState.graph.series[2].threshold : '',
            series4threshold: panelState.graph.series?.length > 3 ? panelState.graph.series[3].threshold : '',
            series1dataSource: panelState.graph.series?.length > 0 ? panelState.graph.series[0].dataSource : '',
            series1dataPointField: panelState.graph.series?.length > 0 ? panelState.graph.series[0].dataPointField : '',
            series1dateField: panelState.graph.series?.length > 0 ? panelState.graph.series[0].dateField : '',
            series2dataSource: panelState.graph.series?.length > 1 ? panelState.graph.series[1].dataSource : '',
            series2dataPointField: panelState.graph.series?.length > 1 ? panelState.graph.series[1].dataPointField : '',
            series2dateField: panelState.graph.series?.length > 1 ? panelState.graph.series[1].dateField : '',
            series3dataSource: panelState.graph.series?.length > 2 ? panelState.graph.series[2].dataSource : '',
            series3dataPointField: panelState.graph.series?.length > 2 ? panelState.graph.series[2].dataPointField : '',
            series3dateField: panelState.graph.series?.length > 2 ? panelState.graph.series[2].dateField : '',
            series4dataSource: panelState.graph.series?.length > 3 ? panelState.graph.series[3].dataSource : '',
            series4dataPointField: panelState.graph.series?.length > 3 ? panelState.graph.series[3].dataPointField : '',
            series4dateField: panelState.graph.series?.length > 3 ? panelState.graph.series[3].dateField : '',
            lineCalculation: panelState.graph.lineCalculation,
            lineThresholdType: panelState.graph.lineThresholdType,
            thresholdNum1: panelState.graph.thresholdNum1 < 0 ? '' : panelState.graph.thresholdNum1,
            thresholdNum2: panelState.graph.thresholdNum2 < 0 ? '' : panelState.graph.thresholdNum2,
            displayUnits: PanelDisplayUnits.PERCENT,
            scheduleId: panelState.scheduleId,
        } as any;
        const errorsState = {
            timePeriod: '',
            seriesType: '',
            numberOfSeries: '',
            series1label: '',
            series2label: '',
            series3label: '',
            series4label: '',
            series1threshold: '',
            series2threshold: '',
            series3threshold: '',
            series4threshold: '',
            series1dataSource: '',
            series1dataPointField: '',
            series1dateField: '',
            series2dataSource: '',
            series2dataPointField: '',
            series2dateField: '',
            series3dataSource: '',
            series3dataPointField: '',
            series3dateField: '',
            series4dataSource: '',
            series4dataPointField: '',
            series4dateField: '',
            lineCalculation: '',
            lineThresholdType: '',
            thresholdNum1: '',
            thresholdNum2: '',
            scheduleId: '',
        } as any;
        const [state, setState] = useState({ ...initialState });
        const [errors, setErrors] = useState({ ...errorsState });
        const scheduleInactive = useBoolean(false);
        const getDataPointOptionsFormats = () => {
            switch (state.seriesType) {
                case PanelSeriesType.SUBJECT:
                    return [Formats.Int, Formats.Float];
                case PanelSeriesType.SITE_AVERAGE:
                    return [Formats.Int, Formats.Float];
                case PanelSeriesType.SUBJECT_BY_VISIT:
                    return [Formats.Int, Formats.Float];
                default:
                    return [Formats.Text];
            }
        };

        const dateOptionsFormats = [Formats.Date, Formats.PartialDate];
        const dataPointOptionsFormats = getDataPointOptionsFormats();

        const timePeriodOptions = Object.keys(PanelLineTimePeriod).map((time: any) => ({
            default: false,
            id: time,
            text: t(`admin:${time}`),
            active: true,
        }));

        useEffect(() => {
            const scheduleOption = scheduleOptions.find((x: any) => x.id === state.scheduleId);
            scheduleInactive.setValue(scheduleOption?.id === state.scheduleId && scheduleOption?.active === false);
        }, [state.scheduleId, scheduleOptions]);

        const numberOfSeriesOptions = graphNumberOfSeries.map((number) => ({
            id: number,
            text: `${number}`,
            active: true,
            default: false,
        }));
        const graphSeriesTypesOptions = useMemo(PanelService.getGraphSeriesTypesOptions, []);
        const lineCalculationTypeOptions = useMemo(PanelService.getLineCalculationTypeOptions, []);
        const averageLineThresholdTypeOptions = useMemo(PanelService.getAverageLineThresholdTypeOptions, []);

        useEffect(() => {
            updateErrorState(errors, 2);
        }, [errors]);

        if (!localState?.selectedStudy) return <Loading />;

        const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
            let error = validateFields(value, field, length, required);
            if (
                field === 'scheduleId' &&
                state.seriesType === PanelSeriesType.SUBJECT_BY_VISIT &&
                value === Constants.ObjectId.Empty
            ) {
                error = { scheduleId: t('ra:requiredFieldError') };
            }
            setErrors({
                ...errors,
                ...error,
            });
        };

        function clearSeriesFrom(seriesNumber: number) {
            const updateValues = {} as any;

            for (let i = seriesNumber; i < graphNumberOfSeries.length + 1; i++) {
                updateState({ target: { name: `graph.series${i}dataSource`, value: '' } });
                updateState({ target: { name: `graph.series${i}dataPointField`, value: '' } });
                updateState({ target: { name: `graph.series${i}dateField`, value: '' } });
                updateState({ target: { name: `graph.series${i}label`, value: '' } });
                updateState({ target: { name: `graph.series${i}threshold`, value: '' } });
                updateValues[`series${i}dataSource`] = '';
                updateValues[`series${i}dataPointField`] = '';
                updateValues[`series${i}dateField`] = '';
                updateValues[`series${i}label`] = '';
                updateValues[`series${i}threshold`] = '';
            }

            clearGraphSeriesState(seriesNumber);

            return updateValues;
        }

        const onChangeNumberOfSeries = (value: number) => {
            const updateValues = clearSeriesFrom(value + 1);
            setState({ ...state, ...updateValues, numberOfSeries: value });
        };

        const onChangeSeriesType = (value: any) => {
            const updateValues = clearSeriesFrom(1);

            setState({
                ...state,
                ...updateValues,
                seriesType: value,
                timePeriod:
                    value === PanelSeriesType.SUBJECT || value === PanelSeriesType.SITE_AVERAGE
                        ? PanelLineTimePeriod.WEEKS
                        : state.timePeriod,
                numberOfSeries: 1,
                lineCalculation: PanelLineCalculation.MEAN,
                lineThresholdType: PanelAverageLineThresholdType.STUDY_MEDIAN_MEAN,
                thresholdNum1:
                    value === PanelSeriesType.SUBJECT_BY_VISIT || value === PanelSeriesType.SITE_AVERAGE ? '' : 0,
                thresholdNum2:
                    value === PanelSeriesType.SUBJECT_BY_VISIT || value === PanelSeriesType.SITE_AVERAGE ? '' : 0,
            });

            updateState({
                target: {
                    name: 'graph.timePeriod',
                    value: value === PanelSeriesType.SUBJECT ? PanelLineTimePeriod.WEEKS : state.timePeriod,
                },
            });

            if (value.length && value !== 'NONE') {
                setErrors({
                    ...errorsState,
                });
            }
        };

        const onChangeSeriesLabel = (fieldName: string, value: any, seriesNumber: string, seriesIndex: number) => {
            setState({ ...state, [fieldName]: value });

            setGraphSeriesState(+seriesNumber, {
                ...panelState.graph.series[seriesIndex],
                label: value,
            });
        };

        const onChangeSeriesDataSource = (seriesNumber: string, value: any, seriesIndex: number) => {
            setState({
                ...state,
                [`series${seriesNumber}dataSource`]: value,
                [`series${seriesNumber}dataPointField`]: '',
                [`series${seriesNumber}dateField`]: '',
            });

            setGraphSeriesState(+seriesNumber, {
                ...panelState.graph.series[seriesIndex],
                dataSource: value,
                dataPointField: '',
                dateField: '',
            });
        };

        const onChangeSeriesDataPointField = (seriesNumber: string, value: any, seriesIndex: number) => {
            setState({
                ...state,
                [`series${seriesNumber}dataPointField`]: value,
                [`series${seriesNumber}dateField`]: '',
            });

            setGraphSeriesState(+seriesNumber, {
                ...panelState.graph.series[seriesIndex],
                dataPointField: value,
                dateField: '',
            });
        };

        const onChangeSeriesDateField = (fieldName: string, value: any, seriesNumber: string, seriesIndex: number) => {
            setState({ ...state, [fieldName]: value });

            setGraphSeriesState(+seriesNumber, {
                ...panelState.graph.series[seriesIndex],
                dateField: value,
            });
        };

        const onChangeSeriesThreshold = (value: any, fieldName: string, seriesNumber: string, seriesIndex: number) => {
            // We want to allow only integer numbers
            if (value.match('.')) value = parseInt(value, 10);

            setState({ ...state, [fieldName]: value });

            setGraphSeriesState(+seriesNumber, {
                ...panelState.graph.series[seriesIndex],
                threshold: value,
            });
        };

        const onChangeLineThresholdType = (value: any) => {
            setState({
                ...state,
                lineThresholdType: value,
                thresholdNum1: '',
                thresholdNum2: '',
            });
        };

        const onChange = (fieldName: string, length?: number, required?: boolean, key?: string, value?: any) => {
            const match = fieldName?.match(/\d+/);
            const seriesNumber = match?.length && match[0];
            const seriesIndex = +seriesNumber - 1;

            if (fieldName === 'numberOfSeries') {
                onChangeNumberOfSeries(value);
            } else if (fieldName === 'seriesType') {
                onChangeSeriesType(value);
            } else if (seriesNumber && fieldName === `series${seriesNumber}label`) {
                onChangeSeriesLabel(fieldName, value, seriesNumber, seriesIndex);
            } else if (seriesNumber && fieldName === `series${seriesNumber}dataSource`) {
                onChangeSeriesDataSource(seriesNumber, value, seriesIndex);
            } else if (seriesNumber && fieldName === `series${seriesNumber}dataPointField`) {
                onChangeSeriesDataPointField(seriesNumber, value, seriesIndex);
            } else if (seriesNumber && fieldName === `series${seriesNumber}dateField`) {
                onChangeSeriesDateField(fieldName, value, seriesNumber, seriesIndex);
            } else if (seriesNumber && fieldName === `series${seriesNumber}threshold`) {
                onChangeSeriesThreshold(value, fieldName, seriesNumber, seriesIndex);
            } else if (fieldName === 'lineThresholdType') onChangeLineThresholdType(value);
            else setState({ ...state, [fieldName]: value });

            updateState({ target: { name: key || fieldName, value } });
            validate(value, fieldName, length, required);
        };

        const validateThreshold = (fieldName: string, value: any) => {
            let error = {};
            const invalidError: string =
                state.seriesType === 'SUBJECT_BY_VISIT'
                    ? t('admin:upperThresholdShouldBeBiggerThanLower')
                    : t('admin:redThresholdShouldBeBiggerThanAmber');

            if (value < -1)
                error = {
                    thresholdNum1: invalidError,
                };
            if (fieldName === 'thresholdNum1' && value >= state.thresholdNum2) error = { thresholdNum1: invalidError };
            if (fieldName === 'thresholdNum1' && value < state.thresholdNum2)
                error =
                    value !== '' && value >= 0
                        ? { thresholdNum1: '', thresholdNum2: '' }
                        : { thresholdNum1: invalidError };

            if (fieldName === 'thresholdNum2' && value <= state.thresholdNum1) error = { thresholdNum2: invalidError };
            if (fieldName === 'thresholdNum2' && value > state.thresholdNum1)
                error =
                    value !== '' && +value !== 0
                        ? { thresholdNum1: '', thresholdNum2: '' }
                        : { thresholdNum2: invalidError };
            if (
                (fieldName === 'thresholdNum1' && value === '' && state.thresholdNum2 === '') ||
                (fieldName === 'thresholdNum2' && value === '' && state.thresholdNum1 === '')
            )
                error = { thresholdNum1: '', thresholdNum2: '' };
            setErrors({ ...errors, ...error });
        };

        const onChangeThreshold = (fieldName: string, key?: string) => (event: any) => {
            const { value }: any = event.target;

            setState({ ...state, [fieldName]: value === '' ? '' : +value });
            updateState({ target: { name: key || fieldName, value: value === '' ? '' : +value } });
            validate(value, fieldName, null, true);

            validateThreshold(fieldName, value);
        };
        const setThresholdsBeforeSave = () => {
            if (state.thresholdNum1 === '' && state.thresholdNum2 === '') {
                updateState({ target: { name: 'graph.thresholdNum1' || 'thresholdNum1', value: -1 } });
                updateState({ target: { name: 'graph.thresholdNum2' || 'thresholdNum2', value: -1 } });
            }
        };

        const validationOnUpdate = () => {
            let requiredFields = ['seriesType'];
            let isThresholdValid = true;
            const isDefaultScheduleId =
                state.seriesType === PanelSeriesType.SUBJECT_BY_VISIT && state.scheduleId === Constants.ObjectId.Empty;

            if (state.seriesType !== PanelSeriesType.SUBJECT && state.seriesType !== PanelSeriesType.SUBJECT_BY_VISIT)
                requiredFields.push('timePeriod');

            if (state.seriesType === PanelSeriesType.SITE || state.seriesType === PanelSeriesType.SITE_CUMULATIVE) {
                const seriesTypeRequired = ['numberOfSeries'];
                Array.from({ length: state.numberOfSeries }, (_, index) => {
                    const number = index + 1;
                    seriesTypeRequired.push(`series${number}label`);
                    seriesTypeRequired.push(`series${number}dataSource`);
                    seriesTypeRequired.push(`series${number}dateField`);
                });
                requiredFields = requiredFields.concat(seriesTypeRequired);
            } else if (
                state.seriesType === PanelSeriesType.EVENT ||
                state.seriesType === PanelSeriesType.SUBJECT ||
                state.seriesType === PanelSeriesType.SITE_AVERAGE
            )
                requiredFields.push('series1dataSource', 'series1dataPointField', 'series1dateField');

            if (
                state.seriesType === PanelSeriesType.SUBJECT_BY_VISIT ||
                state.seriesType === PanelSeriesType.SITE_AVERAGE
            ) {
                if (
                    state.thresholdNum1 >= state.thresholdNum2 &&
                    state.thresholdNum1 !== '' &&
                    state.thresholdNum2 !== ''
                ) {
                    validateThreshold('thresholdNum1', state.thresholdNum1);
                    isThresholdValid = false;
                }
                if (
                    (state.thresholdNum1 === '' && state.thresholdNum2 !== '') ||
                    (state.thresholdNum1 !== '' && state.thresholdNum2 === '') ||
                    (state.thresholdNum1 < 0 && state.thresholdNum2 > 0)
                ) {
                    validateThreshold('thresholdNum1', state.thresholdNum1);
                    isThresholdValid = false;
                }
                if (state.thresholdNum1 === '' && state.thresholdNum2 === '') {
                    const error = { thresholdNum1: '', thresholdNum2: '' };
                    setErrors({ ...errors, ...error });
                    setThresholdsBeforeSave();
                }

                if (state.seriesType === PanelSeriesType.SUBJECT_BY_VISIT) {
                    requiredFields.push('series1dataSource', 'series1dataPointField', 'scheduleId');
                }
                if (state.seriesType === PanelSeriesType.SITE_AVERAGE) {
                    requiredFields.push('lineCalculation', 'lineThresholdType');
                    if (state.lineThresholdType === PanelAverageLineThresholdType.STUDY_SPECIFIC)
                        requiredFields.push('thresholdNum1', 'thresholdNum2');
                }
            }

            const updateErrors = (errs: { [key: string]: string }[]) => {
                setErrors({
                    ...errs,
                    ...(isDefaultScheduleId ? { scheduleId: t('ra:requiredFieldError') } : {}),
                });
            };

            return (
                isThresholdValid &&
                checkRequiredFields({ fields: requiredFields, state, setErrors: updateErrors, errors }) &&
                !isDefaultScheduleId
            );
        };

        const onNext = () => {
            if (validationOnUpdate()) {
                setPage({ ...page, number: 3, available: 3 });
            } else if (page.number !== 2) setPage({ ...page, number: 2, available: page.available });
        };

        const cleanStep = () => {
            setState({ ...initialState });
            setErrors({ ...errorsState });
        };

        useImperativeHandle(ref, () => ({
            cleanStep() {
                cleanStep();
            },
            nextButton() {
                onNext();
            },
            validationOnUpdate() {
                return validationOnUpdate();
            },
        }));

        return (
            <div style={!show ? { display: 'none' } : {}}>
                <div className="step2-line">
                    <div className="type-edc-wrapper mt">
                        <FormControl variant="outlined" className="form-control-type mr">
                            <SelectWithCategories
                                customClassName="add-panel-seriesType"
                                showWarning={false}
                                fieldSelect="seriesType"
                                onChange={(fieldName?: string) => (event: any) => {
                                    onChange(fieldName, null, false, 'graph.seriesType', event.target?.value);
                                }}
                                label={t('admin:seriesTypes')}
                                filterable={false}
                                labelField="seriesType"
                                dataArray={graphSeriesTypesOptions}
                                customId="add-panel-seriesType"
                                currentState={state.seriesType}
                                error={errors.seriesType}
                                disabled={!canSave}
                            />
                        </FormControl>
                        <FormControl variant="outlined" className="form-control-type">
                            {state.seriesType !== PanelSeriesType.SUBJECT &&
                            state.seriesType !== PanelSeriesType.SUBJECT_BY_VISIT ? (
                                <SelectWithCategories
                                    label={t('admin:xAxisTimePeriod')}
                                    dataArray={timePeriodOptions}
                                    onChange={(fieldName?: string) => (event: any) => {
                                        onChange(fieldName, null, false, 'graph.timePeriod', event.target?.value);
                                    }}
                                    fieldSelect="timePeriod"
                                    currentState={state.timePeriod}
                                    labelField="timePeriod"
                                    showWarning={false}
                                    filterable={false}
                                    customLabelId="add-panel-timePeriod-label"
                                    customId="add-panel-timePeriod"
                                    customClassName="add-panel-timePeriod"
                                    error={errors.timePeriod}
                                />
                            ) : null}
                        </FormControl>
                    </div>
                    {(state.seriesType === PanelSeriesType.SITE ||
                        state.seriesType === PanelSeriesType.SITE_CUMULATIVE) && (
                        <>
                            <div style={{ marginTop: 37 }} className="type-edc-wrapper">
                                <FormControl variant="outlined" className="form-control-type not-full">
                                    <SelectWithCategories
                                        label={t('admin:numberOfSeriesR')}
                                        dataArray={numberOfSeriesOptions}
                                        onChange={(fieldName?: string) => (event: any) => {
                                            onChange(
                                                fieldName,
                                                null,
                                                false,
                                                'graph.numberOfSeries',
                                                event.target?.value,
                                            );
                                        }}
                                        fieldSelect="numberOfSeries"
                                        currentState={state.numberOfSeries}
                                        labelField="numberOfSeries"
                                        showWarning={false}
                                        filterable={false}
                                        customLabelId="add-panel-numberOfSeries-label"
                                        customId="add-panel-numberOfSeries"
                                        customClassName="add-panel-numberOfSeries"
                                        error={errors.numberOfSeries}
                                        disabled={!canSave}
                                    />
                                </FormControl>
                            </div>
                            {Array.from({ length: state.numberOfSeries }, (_, index) => {
                                const number = index + 1;
                                return (
                                    <div key={`numberOfSeries-${index}`}>
                                        <div style={{ marginTop: 35 }} className="type-edc-wrapper">
                                            <FormControl variant="outlined" className="form-control-type not-full mr">
                                                <TextField
                                                    label={t('admin:seriesNumberLabelR', { number })}
                                                    id={`add-panel-series${number}label-shortName`}
                                                    fullWidth
                                                    value={state[`series${number}label`] ?? ''}
                                                    onChange={(event: any) =>
                                                        onChange(
                                                            `series${number}label`,
                                                            50,
                                                            false,
                                                            `graph.series${number}label`,
                                                            event.target?.value,
                                                        )
                                                    }
                                                    placeholder={t('admin:enterPlotTitle')}
                                                    helperText={
                                                        <CharactersRemaining
                                                            id={`add-panel-series${number}label-shortName-help-text`}
                                                            length={state[`series${number}label`]?.length}
                                                            max={50}
                                                            error={errors[`series${number}label`]}
                                                        />
                                                    }
                                                    error={!!errors[`series${number}label`]}
                                                    variant="outlined"
                                                    color="secondary"
                                                    className="before-select"
                                                />
                                            </FormControl>
                                            {state.seriesType === PanelSeriesType.SITE_CUMULATIVE ? (
                                                <FormControl variant="outlined" className="form-control-type not-full">
                                                    <TextField
                                                        label={t('admin:seriesNumberThresholdR', { number })}
                                                        id={`add-panel-series${number}label-threshold`}
                                                        fullWidth
                                                        value={state[`series${number}threshold`] || ''}
                                                        onChange={(event: any) =>
                                                            onChange(
                                                                `series${number}threshold`,
                                                                50,
                                                                false,
                                                                `graph.series${number}threshold`,
                                                                event.target?.value,
                                                            )
                                                        }
                                                        placeholder={t('admin:enterThreshold')}
                                                        type="number"
                                                        slotProps={{ htmlInput: { min: 0, step: 1 } }}
                                                        error={!!errors[`series${number}threshold`]}
                                                        variant="outlined"
                                                        color="secondary"
                                                        className="before-select"
                                                    />
                                                </FormControl>
                                            ) : null}
                                        </div>
                                        <div className="type-edc-wrapper">
                                            <FormControl variant="outlined" className="form-control-type mr">
                                                <DataSourceSelector
                                                    customClassName={`add-panel-series${number}rowDataSource`}
                                                    error={errors[`series${number}dataSource`]}
                                                    fieldSelect={`series${number}dataSource`}
                                                    onChange={(value: string) => {
                                                        onChange(
                                                            `series${number}dataSource`,
                                                            null,
                                                            false,
                                                            `graph.series${number}dataSource`,
                                                            value,
                                                        );
                                                    }}
                                                    label={t('admin:seriesNumberRowDataR', { number })}
                                                    labelField={`series${number}dataSource`}
                                                    value={state[`series${number}dataSource`]}
                                                    showDefault={false}
                                                    tag={RowDataSourceTag.PANEL}
                                                    searchable
                                                    disabled={!canSave}
                                                />
                                            </FormControl>
                                            <FormControl variant="outlined" className="form-control-type">
                                                <FieldSelector
                                                    label={t('admin:seriesNumberDateR', { number })}
                                                    dataSource={state[`series${number}dataSource`]}
                                                    formats={dateOptionsFormats}
                                                    value={state[`series${number}dateField`]}
                                                    onChange={(value: string) => {
                                                        onChange(
                                                            `series${number}dateField`,
                                                            null,
                                                            false,
                                                            `graph.series${number}dateField`,
                                                            value,
                                                        );
                                                    }}
                                                    labelField={`series${number}dateField`}
                                                    fieldSelect={`series${number}dateField`}
                                                    customId={`add-panel-series${number}dateField`}
                                                    error={errors[`series${number}dateField`]}
                                                    searchable
                                                    disabled={!canSave}
                                                />
                                            </FormControl>
                                        </div>
                                    </div>
                                );
                            })}
                        </>
                    )}
                    {state.seriesType === PanelSeriesType.SUBJECT || state.seriesType === PanelSeriesType.EVENT ? (
                        <>
                            <div className="type-edc-wrapper mt" style={{ marginBottom: 35 }}>
                                <FormControl variant="outlined" className="form-control-type">
                                    <DataSourceSelector
                                        customClassName="add-panel-seriesRowData"
                                        error={errors.series1dataSource}
                                        showDefault={false}
                                        fieldSelect="series1dataSource"
                                        onChange={(value: string) => {
                                            onChange(
                                                'series1dataSource',
                                                null,
                                                false,
                                                'graph.series1dataSource',
                                                value,
                                            );
                                        }}
                                        label={t('admin:seriesRowDataR')}
                                        labelField="series1dataSource"
                                        value={state.series1dataSource}
                                        tables={
                                            state.seriesType === PanelSeriesType.SUBJECT
                                                ? [
                                                      RowDataSourceTable.SUBJECT_VISIT,
                                                      RowDataSourceTable.SUBJECT_PROTOCOL_DEVIATION,
                                                      RowDataSourceTable.SUBJECT_INFORMED_CONSENT,
                                                      RowDataSourceTable.SUBJECT_ASSESSMENT,
                                                      RowDataSourceTable.SUBJECT_ADVERSE_EVENT,
                                                      RowDataSourceTable.SUBJECT_CRF,
                                                      RowDataSourceTable.SUBJECT_ISSUE,
                                                      RowDataSourceTable.SUBJECT_LAB,
                                                      RowDataSourceTable.SUBJECT_QUERY,
                                                  ]
                                                : null
                                        }
                                        tag="PANEL"
                                        searchable
                                        disabled={!canSave}
                                    />
                                </FormControl>
                            </div>
                            <div className="type-edc-wrapper">
                                <FormControl variant="outlined" className="form-control-type mr">
                                    <FieldSelector
                                        label={t('admin:seriesDataPointR')}
                                        dataSource={state.series1dataSource}
                                        formats={dataPointOptionsFormats}
                                        value={state.series1dataPointField}
                                        onChange={(value: string) => {
                                            onChange(
                                                'series1dataPointField',
                                                null,
                                                false,
                                                'graph.series1dataPointField',
                                                value,
                                            );
                                        }}
                                        labelField="series1dataPointField"
                                        fieldSelect="series1dataPointField"
                                        customId="add-panel-series1dataPointField"
                                        error={errors.series1dataPointField}
                                        searchable
                                        disabled={!canSave}
                                    />
                                </FormControl>
                                <FormControl variant="outlined" className="form-control-type">
                                    <FieldSelector
                                        label={t('admin:seriesDateR')}
                                        dataSource={state.series1dataSource}
                                        formats={dateOptionsFormats}
                                        value={state.series1dateField}
                                        onChange={(value: string) => {
                                            onChange('series1dateField', null, false, 'graph.series1dateField', value);
                                        }}
                                        labelField="series1dateField"
                                        fieldSelect="series1dateField"
                                        customId="add-panel-seriesType"
                                        error={errors.series1dateField}
                                        searchable
                                        disabled={!canSave}
                                    />
                                </FormControl>
                            </div>
                        </>
                    ) : null}
                    {state.seriesType === PanelSeriesType.SITE_AVERAGE && (
                        <>
                            <div className="type-edc-wrapper mt">
                                <FormControl variant="outlined" className="form-control-type mr">
                                    <DataSourceSelector
                                        customClassName="add-panel-rowData"
                                        error={errors.series1dataSource}
                                        showDefault={false}
                                        fieldSelect="series1dataSource"
                                        onChange={(value: string) => {
                                            onChange(
                                                'series1dataSource',
                                                null,
                                                false,
                                                'graph.series1dataSource',
                                                value,
                                            );
                                        }}
                                        label={t('admin:rowDataR')}
                                        labelField="series1dataSource"
                                        value={state.series1dataSource}
                                        tag="PANEL"
                                        tables={Object.values(RowDataSourceTable)}
                                        searchable
                                        disabled={!canSave}
                                    />
                                </FormControl>
                                <FormControl variant="outlined" className="form-control-type">
                                    <FieldSelector
                                        label={t('admin:dataPointR')}
                                        dataSource={state.series1dataSource}
                                        formats={dataPointOptionsFormats}
                                        value={state.series1dataPointField}
                                        onChange={(value: string) => {
                                            onChange(
                                                'series1dataPointField',
                                                null,
                                                false,
                                                'graph.series1dataPointField',
                                                value,
                                            );
                                        }}
                                        labelField="series1dataPointField"
                                        fieldSelect="series1dataPointField"
                                        customId="add-panel-dataPoint"
                                        customClassName="add-panel-dataPoint"
                                        error={errors.series1dataPointField}
                                        searchable
                                        disabled={!canSave}
                                    />
                                </FormControl>
                            </div>
                            <div className="type-edc-wrapper mt">
                                <FormControl variant="outlined" className="form-control-type mr">
                                    <FieldSelector
                                        label={t('admin:sortByDate')}
                                        dataSource={state.series1dataSource}
                                        formats={dateOptionsFormats}
                                        value={state.series1dateField}
                                        onChange={(value: string) => {
                                            onChange('series1dateField', null, false, 'graph.series1dateField', value);
                                        }}
                                        labelField="series1dateField"
                                        fieldSelect="series1dateField"
                                        customId="add-panel-sortByDate"
                                        customClassName="add-panel-sortByDate"
                                        error={errors.series1dateField}
                                        searchable
                                        disabled={!canSave}
                                    />
                                </FormControl>
                                <FormControl variant="outlined" className="form-control-type">
                                    <SelectWithCategories
                                        label={t('admin:lineCalculation')}
                                        dataArray={lineCalculationTypeOptions}
                                        onChange={(fieldName?: string) => (event: any) => {
                                            onChange(
                                                fieldName,
                                                null,
                                                false,
                                                'graph.lineCalculation',
                                                event.target?.value,
                                            );
                                        }}
                                        fieldSelect="lineCalculation"
                                        currentState={state.lineCalculation}
                                        labelField="lineCalculation"
                                        showWarning={false}
                                        filterable={false}
                                        customLabelId="add-panel-lineCalculation-label"
                                        customId="add-panel-lineCalculation"
                                        customClassName="add-panel-lineCalculation"
                                        error={errors.lineCalculation}
                                    />
                                </FormControl>
                            </div>
                            <div className="type-edc-wrapper mt">
                                <FormControl variant="outlined" className="form-control-type not-full">
                                    <SelectWithCategories
                                        label={t('admin:thresholdType')}
                                        dataArray={averageLineThresholdTypeOptions}
                                        onChange={(fieldName: string) => (event: any) => {
                                            onChange(
                                                fieldName,
                                                null,
                                                false,
                                                'graph.lineThresholdType',
                                                event.target?.value,
                                            );
                                        }}
                                        fieldSelect="lineThresholdType"
                                        currentState={state.lineThresholdType}
                                        labelField="lineThresholdType"
                                        showWarning={false}
                                        filterable={false}
                                        customLabelId="add-panel-lineThresholdType-label"
                                        customId="add-panel-lineThresholdType"
                                        customClassName="add-panel-lineThresholdType"
                                        error={errors.lineThresholdType}
                                    />
                                </FormControl>
                            </div>
                            {state.lineThresholdType === PanelAverageLineThresholdType.STUDY_SPECIFIC && (
                                <OutlinedWrapper customClass="mb" label={t('admin:thresholds')}>
                                    <div className="type-edc-wrapper">
                                        <FormControl variant="outlined" className="form-control-type mr">
                                            <TextField
                                                label={t('admin:amberThresholdValue')}
                                                id="amber-threshold-value"
                                                fullWidth
                                                value={state.thresholdNum1}
                                                onChange={onChangeThreshold('thresholdNum1', 'graph.thresholdNum1')}
                                                placeholder={t('enterValue')}
                                                helperText={
                                                    <CharactersRemaining
                                                        id="amber-threshold-value-help-text"
                                                        error={errors.thresholdNum1}
                                                    />
                                                }
                                                variant="outlined"
                                                type="number"
                                                color="secondary"
                                                className="before-select"
                                                error={errors.thresholdNum1}
                                            />
                                        </FormControl>
                                        <FormControl variant="outlined" className="form-control-type">
                                            <TextField
                                                label={t('admin:redThresholdValue')}
                                                id="red-threshold-value"
                                                fullWidth
                                                value={state.thresholdNum2}
                                                onChange={onChangeThreshold('thresholdNum2', 'graph.thresholdNum2')}
                                                placeholder={t('enterValue')}
                                                helperText={
                                                    <CharactersRemaining
                                                        id="red-threshold-value-help-text"
                                                        error={errors.thresholdNum2}
                                                    />
                                                }
                                                variant="outlined"
                                                color="secondary"
                                                type="number"
                                                className="before-select"
                                                error={errors.thresholdNum2}
                                            />
                                        </FormControl>
                                    </div>
                                </OutlinedWrapper>
                            )}
                        </>
                    )}
                    {state.seriesType === PanelSeriesType.SUBJECT_BY_VISIT ? (
                        <>
                            <div className="type-edc-wrapper  mb-30">
                                <FormControl variant="outlined" className="form-control-type mr">
                                    <DataSourceSelector
                                        customClassName="add-panel-seriesRowData"
                                        error={errors.series1dataSource}
                                        showDefault={false}
                                        fieldSelect="series1dataSource"
                                        onChange={(value: string) => {
                                            onChange(
                                                'series1dataSource',
                                                null,
                                                false,
                                                'graph.series1dataSource',
                                                value,
                                            );
                                        }}
                                        label={t('admin:seriesRowDataR')}
                                        labelField="series1dataSource"
                                        value={state.series1dataSource}
                                        tables={
                                            state.seriesType === PanelSeriesType.SUBJECT_BY_VISIT
                                                ? [
                                                      RowDataSourceTable.SUBJECT_VISIT,
                                                      RowDataSourceTable.SUBJECT_ASSESSMENT,
                                                      RowDataSourceTable.SUBJECT_CRF,
                                                      RowDataSourceTable.SUBJECT_LAB,
                                                      RowDataSourceTable.SUBJECT_QUERY,
                                                  ]
                                                : null
                                        }
                                        tag="PANEL"
                                        searchable
                                        disabled={!canSave}
                                    />
                                </FormControl>
                                <FormControl variant="outlined" className="form-control-type">
                                    <FieldSelector
                                        label={t('admin:seriesDataPointR')}
                                        dataSource={state.series1dataSource}
                                        formats={dataPointOptionsFormats}
                                        value={state.series1dataPointField}
                                        onChange={(value: string) => {
                                            onChange(
                                                'series1dataPointField',
                                                null,
                                                false,
                                                'graph.series1dataPointField',
                                                value,
                                            );
                                        }}
                                        labelField="series1dataPointField"
                                        fieldSelect="series1dataPointField"
                                        customId="add-panel-series1dataPointField"
                                        error={errors.series1dataPointField}
                                        searchable
                                        disabled={!canSave}
                                    />
                                </FormControl>
                            </div>
                            <div className="type-edc-wrapper mt  mb-30">
                                <FormControl variant="outlined" className="form-control-type not-full mr pt-8">
                                    <SelectWithCategories
                                        label={t('admin:scheduleNameR')}
                                        dataArray={scheduleOptions}
                                        onChange={() => (event: any) => {
                                            onChange('scheduleId', null, true, 'scheduleId', event.target?.value);
                                        }}
                                        fieldSelect="scheduleId"
                                        currentState={state.scheduleId}
                                        labelField="schedule"
                                        showWarning={scheduleInactive.value}
                                        customLabelId="schedule-id"
                                        customId="schedule-id"
                                        error={errors.scheduleId}
                                        customClassName={`${
                                            scheduleInactive.value ? 'form-action-type inactive' : 'form-action-type'
                                        }`}
                                    />
                                </FormControl>
                            </div>
                            <OutlinedWrapper customClass="mb" label={t('admin:thresholds')}>
                                <div className="type-edc-wrapper">
                                    <FormControl variant="outlined" className="form-control-type mr">
                                        <TextField
                                            label={t('admin:lowerThresholdValue')}
                                            id="lower-threshold-value"
                                            fullWidth
                                            value={state.thresholdNum1 ?? ''}
                                            onChange={onChangeThreshold('thresholdNum1', 'graph.thresholdNum1')}
                                            placeholder={t('enterValue')}
                                            helperText={
                                                <CharactersRemaining
                                                    id="lower-threshold-value-help-text"
                                                    error={errors.thresholdNum1}
                                                />
                                            }
                                            variant="outlined"
                                            type="number"
                                            color="secondary"
                                            className="before-select"
                                            error={errors.thresholdNum1}
                                        />
                                    </FormControl>
                                    <FormControl variant="outlined" className="form-control-type">
                                        <TextField
                                            label={t('admin:upperThresholdValue')}
                                            id="upper-threshold-value"
                                            fullWidth
                                            value={state.thresholdNum2 ?? ''}
                                            onChange={onChangeThreshold('thresholdNum2', 'graph.thresholdNum2')}
                                            placeholder={t('enterValue')}
                                            helperText={
                                                <CharactersRemaining
                                                    id="upper-threshold-value-help-text"
                                                    error={errors.thresholdNum2}
                                                />
                                            }
                                            variant="outlined"
                                            color="secondary"
                                            type="number"
                                            className="before-select"
                                            error={errors.thresholdNum2}
                                        />
                                    </FormControl>
                                </div>
                            </OutlinedWrapper>
                        </>
                    ) : null}
                </div>
            </div>
        );
    },
);

export default Step2Line;
